import type { CSSProperties } from "react";

import type { AddyThemeTokens } from "../../shared/api/website";

/**
 * Hai luật của Bước 1 trên màn /website, tách ra khỏi component (#19558, #19568, test ở #19600).
 *
 * Lý do tách: cả hai là QUYẾT ĐỊNH, không phải cách vẽ — "theme này hiện màu gì" và "ví dụ trong ô
 * địa chỉ là chữ gì" — và cả hai đã từng sai theo cách không ai thấy khi đọc mã: sáu theme cùng ra
 * một khối đen, và ô địa chỉ gợi ý slug thật của một phòng khám khác. Một luật nằm trong JSX thì
 * chỉ kiểm được bằng cách dựng cả trang (react-query, router, mock API) — đắt, chậm trong jsdom, và
 * cái đỏ lên hiếm khi là luật. Nằm ở đây thì kiểm trực tiếp được.
 */

/** Tiền tố cố định của slug công khai; phòng khám chỉ nhập phần sau nó. */
export const SLUG_PREFIX = "pk-";

/** Ví dụ hiện mờ trong ô "tên phòng khám trong địa chỉ web" khi phòng khám chưa nhập gì. */
export const SLUG_PLACEHOLDER_FALLBACK = "ten-phong-kham";

/**
 * Ví dụ trong ô địa chỉ = slug của CHÍNH phòng khám này, đã bỏ tiền tố.
 *
 * <p>#19558 mục 2: trước đây ô này gợi ý cứng `"luongngoc"` — slug THẬT của một phòng khám khác.
 * Một người dùng cẩn thận sẽ đọc nó như một giá trị hệ thống đề nghị cho mình; người khác tưởng
 * mình đang xem dữ liệu của phòng khám khác. Cả hai cách hiểu đều tệ, và không cách nào là ý định.
 *
 * <p>Dự phòng `ten-phong-kham` cho trường hợp phòng khám chưa có slug nào: một chỗ trống không gợi
 * ý gì thì người dùng không biết định dạng mong đợi (chữ thường, gạch nối, không dấu).
 */
export function slugPlaceholder(ownSlug: string | null | undefined): string {
  const slug = (ownSlug ?? "").trim();
  const withoutPrefix = slug.startsWith(SLUG_PREFIX) ? slug.slice(SLUG_PREFIX.length) : slug;
  return withoutPrefix || SLUG_PLACEHOLDER_FALLBACK;
}

/** Hình dạng thẻ xem trước của một theme: cao bao nhiêu, chia mấy cột. Màu KHÔNG ở đây. */
export type PreviewSpec = {
  hero: CSSProperties;
  rowClass: string;
  cellStyle?: CSSProperties;
};

/** Màu đã chốt cho một thẻ xem trước: nền, mực, dải hero, ba ô nội dung. */
export type PreviewColors = {
  background: string;
  ink: string;
  hero: CSSProperties;
  cells: [string, string, string];
};

/** Màu khi theme không công bố tokens — đúng màu thẻ đã dùng trước #19568. */
const NEUTRAL_BACKGROUND = "#ffffff";
const NEUTRAL_INK = "#1e293b";

/**
 * Màu của thẻ xem trước, lấy từ `tokens` mà addy-sites công bố cho TỪNG theme.
 *
 * <p>#19568: thẻ từng tô cứng `bg-slate-800` cho mọi ô nội dung, và chỉ 4 trong 10 theme có màu
 * hero riêng — nên sáu theme còn lại hiện y hệt nhau, ba khối đen. Phòng khám chọn giao diện bằng
 * cách nhìn, nên sáu lựa chọn trông giống nhau tức là không có lựa chọn nào.
 *
 * <p>Thiếu `tokens` (bản dự phòng của HIS khi Sites không trả lời) thì giữ nguyên màu trung tính cũ
 * thay vì vẽ một bảng màu bịa: một thẻ xám nói "chưa biết màu", một thẻ màu sai nói sai.
 *
 * <p>`brand2` và `accent` có thể vắng trong khi `brand` có: khi ấy mọi chỗ lùi về `brand` chứ không
 * về màu trung tính, nếu không một theme đơn sắc sẽ hiện nửa màu nửa xám.
 */
export function previewColors(spec: PreviewSpec, tokens?: AddyThemeTokens): PreviewColors {
  const ink = tokens?.ink ?? NEUTRAL_INK;
  const brand = tokens?.brand;
  if (!brand) {
    return {
      background: tokens?.bg ?? NEUTRAL_BACKGROUND,
      ink,
      hero: spec.hero,
      cells: [ink, ink, ink],
    };
  }
  const brand2 = tokens.brand2 ?? brand;
  const accent = tokens.accent ?? brand;
  return {
    background: tokens.bg ?? NEUTRAL_BACKGROUND,
    ink,
    hero: { ...spec.hero, background: `linear-gradient(135deg, ${brand}, ${brand2})` },
    cells: [brand2, accent, brand],
  };
}
